import { Component, Input } from '@angular/core';

import { TrColumn } from '@shared/restaurant/models';
import { FrontendModalService } from '@tablesready/frontend-modal';

/**
 * Show/hide table columns. Opened as an Ionic modal via `FrontendModalService`
 * (the app-wide modal convention).
 *
 * The modal deliberately owns no column state of its own. It reads the table's
 * *current* columns through {@link getColumns} on every change detection, and
 * asks the table to make the change by key. A snapshot would go stale the moment
 * the host rebuilt its columns — which the waitlist does on every change, because
 * saving the layout to the location comes back as a `locationUpdated` event and
 * `buildColumns()` then produces brand-new `TrColumn` objects. Toggles made after
 * that point would mutate orphaned objects and appear to do nothing.
 */
@Component({
  selector: 'tablesready-column-visibility-modal',
  templateUrl: './column-visibility-modal.component.html',
  styleUrls: ['./column-visibility-modal.component.scss'],
  standalone: false,
})
export class ColumnVisibilityModalComponent {
  /** Live view of the table's columns, including hidden ones. */
  @Input() getColumns: () => TrColumn[] = () => [];
  /** Ask the table to hide/show one column by key. */
  @Input() setHidden?: (key: string, hidden: boolean) => void;
  /** Ask the table to reveal every column. */
  @Input() showAllColumns?: () => void;
  /** Explanation per column key, shown as an info icon beside the name. */
  @Input() hints: Record<string, string> = {};

  constructor(private readonly modalService: FrontendModalService) {}

  get columns(): TrColumn[] {
    return this.getColumns();
  }

  /** "Show all" has something to do only while at least one column is hidden. */
  get anyHidden(): boolean {
    return this.columns.some(c => c.hidden);
  }

  hint(col: TrColumn): string | null {
    return this.hints?.[col.key] || null;
  }

  /** Hiding this column would leave the table empty — keep it locked on. */
  isLastVisible(col: TrColumn): boolean {
    return !col.hidden && this.columns.filter((c) => !c.hidden).length <= 1;
  }

  toggle(col: TrColumn): void {
    if (this.isLastVisible(col)) {
      return;
    }
    this.setHidden?.(col.key, !col.hidden);
  }

  showAll(): void {
    this.showAllColumns?.();
  }

  close(): void {
    this.modalService.dismiss();
  }
}
